<script setup lang="ts">
import { CirclePlus, Fold } from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { computed, onMounted, ref } from "vue";
import {
  EmailTemplate,
  EmailTemplateDto,
  FormDefinition,
  FormResponseStatus,
  SysOrgRoleName,
  WorkflowDefinitionListDto,
} from "~/api";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { Slotted } from "~/types/Slotted";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";
import EmailTemplateResponseViewer from "../components/EmailTemplateResponseViewer.vue";

type listItemType = EmailTemplateDto;
type mainType = EmailTemplate;
const loading = ref(true);

const props = defineProps<{
  workflowDefinitionGuid?: string;
}>();

const templates = ref<listItemType[]>([]);
const selectedRow = ref<listItemType | undefined>({} as listItemType);
const drawerOpen = ref(false);
const myInfoStore = useMyInfoStore();

const hasSetupBasicRole = myInfoStore.currentSysOrg.roles.includes(
  SysOrgRoleName.SETUP_BASIC,
);

const handleTemplatesResponse = (res: listItemType[]) => {
  templates.value = res;
  sortList();
  loading.value = false;
};

const refreshList = async () => {
  loading.value = true;
  if (props.workflowDefinitionGuid) {
    return apiClient.emailTemplates
      .getAllEmailTemplates(
        myInfoStore.currentSysOrgGuid,
        props.workflowDefinitionGuid,
        false,
      )
      .then((res) => {
        handleTemplatesResponse(res);
      });
  } else {
    return apiClient.emailTemplates
      .getAllEmailTemplates(myInfoStore.currentSysOrgGuid)
      .then((res) => {
        handleTemplatesResponse(res);
      });
  }
};

onMounted(async () => {
  refreshList();
  await getAllWorkflowDefinitionDtos();
});

const extractListItem = (template: mainType): listItemType => {
  return {
    guid: template.guid,
    name: template.name ?? "",
    formDefinitionGuid: template.formDefinitionGuid ?? "",
    updatedAt: template.updatedAt ?? "",
    sysOrgGuid: template.sysOrgGuid ?? "",
  };
};

const sortList = () => {
  templates.value.sort((a: listItemType, b: listItemType) => {
    return (a.name ?? "").localeCompare(b.name ?? "");
  });
};

/// Show the clicked item in the drawer
const showItem = (row: listItemType) => {
  selectedRow.value = row;
  drawerOpen.value = true;
};

const handleNewTemplateAdded = (item: mainType) => {
  templates.value.push(extractListItem(item));
  selectedRow.value = extractListItem(item);
  sortList();
};

const handleTemplateUpdated = (item: listItemType) => {
  const index = templates.value.findIndex((f) => f.guid === item.guid);
  if (index !== -1) {
    templates.value[index] = item;
  }
  sortList();
};
const emailTemplate = ref<mainType>({
  guid: "",
  sysOrgGuid: "",
  formDefinitionGuid: "",
  name: "",
  formResponse: {
    guid: "",
    formDefinitionGuid: "",
    createdAt: "",
    updatedAt: "",
    status: FormResponseStatus.DRAFT,
    answers: [],
    sysOrgGuid: "",
  },
  // Add other required properties with default values if needed
});

const formBeingEdited = ref<FormDefinition>();
const designerDrawerOpen = ref<boolean>(false);
const lastSavedFormJson = ref<string>("");

const handleEditForm = async (formGuid: string) => {
  await apiClient.formDefinition
    .getFormDefinition(formGuid)
    .then((res) => {
      formBeingEdited.value = res;
      lastSavedFormJson.value = JSON.stringify(res);
      designerDrawerOpen.value = true;
    })
    .catch(showError);
};
const lastSavedJson = ref<string>("");

const updateLastUpdated = () => {
  lastSavedJson.value = JSON.stringify(emailTemplate.value);
};

const workflowDefinitionDtos = ref<WorkflowDefinitionListDto[]>([]);

const getAllWorkflowDefinitionDtos = async () => {
  await apiClient.workflowDefinition
    .getAllWorkflowDefinitionDtos(myInfoStore.currentSysOrgGuid)
    .then((res) => {
      workflowDefinitionDtos.value = res;
      return res;
    })
    .catch(showError);
};

const handleCreateForm = async () => {
  // message box with input for form name
  ElMessageBox.prompt(
    "Please enter a name for the email template",
    "Template Name",
    {
      confirmButtonText: "Create",
      cancelButtonText: "Cancel",
    },
  )
    .then(({ value: emailTemplateName }) => {
      // create form
      apiClient.emailTemplates
        .createEmailTemplate(
          emailTemplateName,
          myInfoStore.currentSysOrgGuid,
          props.workflowDefinitionGuid,
        )
        .then((res) => {
          ElMessage.success("Form added successfully");
          updateLastUpdated();
          selectedRow.value = extractListItem(res);
          drawerOpen.value = true;
          refreshList();
          // open form designer
          if (res.formDefinitionGuid) {
            handleEditForm(res.formDefinitionGuid);
          } else {
            showError("Form definition GUID is missing.");
          }
        })
        .catch(showError);
    })
    .catch(() => {
      ElMessage({
        type: "info",
        message: "Canceled",
      });
    });
};

const closeDrawer = () => {
  drawerOpen.value = false;
  selectedRow.value = undefined;
};

const itemsFiltered = computed(() => {
  // custom per item types
  return templates.value;
});
</script>

<template>
  <div class="flexRow">
    <span class="showingInTable">
      Showing {{ itemsFiltered.length }} of
      {{ templates.length }}
    </span>
    <RefreshButton @click="refreshList" />

    <el-button
      v-if="hasSetupBasicRole"
      :icon="CirclePlus"
      title="Add New Email Template"
      class="addButton"
      @click="handleCreateForm()"
    >
      New Email Template
    </el-button>
  </div>

  <el-table v-loading="loading" :data="itemsFiltered" style="width: 100%">
    <el-table-column prop="name" label="Template Name" width="350">
      <template #default="{ row }: Slotted<listItemType>">
        <el-link :icon="Fold" underline="never" @click="showItem(row)">
          {{ row.name }}
        </el-link>
      </template>
    </el-table-column>
    <el-table-column
      v-if="!props.workflowDefinitionGuid"
      prop="name"
      label="Workflow Definition"
      width="200"
    >
      <template #default="{ row }: Slotted<listItemType>">
        <span>
          {{
            workflowDefinitionDtos.find(
              (w) => w.guid === row.workflowDefinitionGuid,
            )?.name || ""
          }}
        </span>
      </template>
    </el-table-column>
  </el-table>

  <EmailTemplateResponseViewer
    v-if="selectedRow?.guid"
    :key="selectedRow.guid"
    :drawer-open="drawerOpen"
    :email-template-prop="selectedRow"
    :model-value="drawerOpen"
    @close-drawer="closeDrawer"
    @added="handleNewTemplateAdded"
    @update-email-template="handleTemplateUpdated"
    @deleted="refreshList"
  >
  </EmailTemplateResponseViewer>
</template>

<style lang="less"></style>
